import React from "react";
import dayjs from "dayjs";
import logoMombasa from "../../../../core/img/branch-logos/logo_mombasa.jpg";
import logoNairobi from "../../../../core/img/branch-logos/logo_nairobi.jpg";
import logoSystems from "../../../../core/img/branch-logos/logo_systems.jpg";
import logoTexastrack from "../../../../core/img/branch-logos/logo_texastrack.png";
import { STATUS, formatLate, groupByCustomer } from "./attendanceDayReport";

const LOGO_BY_BRANCH = { MOMBASA: logoMombasa, NAIROBI: logoNairobi, SYSTEMS: logoSystems, TEXASTRACK: logoTexastrack };

const STATUS_STYLE = {
  [STATUS.ON_TIME]: { color: "#1E7B4A" },
  [STATUS.LATE]: { color: "#B26A00", fontWeight: 700 },
  [STATUS.NOT_MARKED]: { color: "#C0392B", fontWeight: 700 },
};

const ROW_BG = {
  [STATUS.LATE]: "#FDF3E3",
  [STATUS.NOT_MARKED]: "#FBE9E7",
};

/**
 * Printable day attendance - one template for the preview modal and, through
 * renderToStaticMarkup, the print popup (no stylesheet there, so every style is
 * inline). Same approach as the Duty List print.
 *
 * `rows` arrive already classified and filtered by the modal; this only lays
 * them out: header with what the sheet covers, the counts, then Customer ->
 * slot with the marked time and how late.
 */
const AttendanceDayPrintPreview = ({ rows, summary, title, filterText, reportingText, branchCode, companyName, shadeExceptions }) => {
  const base = { fontFamily: "Arial, Helvetica, sans-serif", color: "#000" };
  const th = { padding: "4px 6px", fontSize: "11.5px", borderBottom: "1.5px solid #000", background: "#eee", textAlign: "left" };
  const td = { padding: "3px 6px", fontSize: "11.5px", borderBottom: "1px solid #ccc", verticalAlign: "top" };
  const groups = groupByCustomer(rows);

  const stat = (label, value, color) => (
    <div style={{ border: "1px solid #bbb", borderRadius: "4px", padding: "4px 10px", minWidth: "80px" }}>
      <div style={{ fontSize: "10px", color: "#555" }}>{label}</div>
      <div style={{ fontSize: "17px", fontWeight: 700, color }}>{value}</div>
    </div>
  );

  return (
    <div style={{ ...base, padding: "10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
        <div>
          <div style={{ fontSize: "17px", fontWeight: 700 }}>{title}</div>
          <div style={{ fontSize: "11.5px" }}>{filterText}</div>
          <div style={{ fontSize: "11.5px" }}>{reportingText}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <img src={LOGO_BY_BRANCH[branchCode] || logoMombasa} alt="" style={{ height: "40px", objectFit: "contain" }} />
          {companyName && <div style={{ fontSize: "11.5px", fontWeight: 700 }}>{companyName}</div>}
          <div style={{ fontSize: "9.5px", color: "#555" }}>Printed {new Date().toLocaleString("en-GB")}</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
        {stat("Total posts", summary.total)}
        {stat("Marked", summary.marked, "#1E7B4A")}
        {stat("On time", summary.onTime, "#1E7B4A")}
        {stat("Late", summary.late, "#B26A00")}
        {stat("Not marked", summary.notMarked, "#C0392B")}
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: "12px", color: "#555" }}>Nothing to print for these filters.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, width: "26px" }}>#</th>
              <th style={th}>Post</th>
              <th style={{ ...th, width: "60px" }}>Code</th>
              <th style={th}>Employee</th>
              <th style={{ ...th, width: "105px" }}>Marked at</th>
              <th style={{ ...th, width: "90px" }}>Status</th>
              <th style={{ ...th, width: "70px" }}>Method</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g.key}>
                <tr>
                  <td colSpan={7} style={{ ...td, fontWeight: 700, background: "#f3f3f3", paddingTop: "5px" }}>
                    {g.custName}
                    <span style={{ fontWeight: 400, color: "#555" }}>
                      {" "}
                      · {[...new Set(g.rows.map((r) => r.area).filter(Boolean))].join(", ") || "—"} · {g.rows.length}
                    </span>
                  </td>
                </tr>
                {g.rows.map((r, idx) => (
                  <tr
                    key={`${r.assignmentId}-${idx}`}
                    style={{ pageBreakInside: "avoid", background: shadeExceptions ? ROW_BG[r.status] : undefined }}
                  >
                    <td style={{ ...td, color: "#555" }}>{idx + 1}</td>
                    <td style={td}>
                      {r.assignmentName || "Duty Slot"}
                      {r.isTemp ? " (T)" : ""}
                    </td>
                    <td style={td}>{r.empCode ?? ""}</td>
                    <td style={td}>{[r.empName, r.empLastName].filter(Boolean).join(" ")}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      {r.capturedDate ? dayjs(r.capturedDate).format("DD MMM h:mm A") : ""}
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap", ...STATUS_STYLE[r.status] }}>
                      {r.status === STATUS.LATE ? `Late ${formatLate(r.lateMinutes)}` : r.status}
                    </td>
                    <td style={{ ...td, fontSize: "10px" }}>{r.matchMethod || ""}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AttendanceDayPrintPreview;
